// ====================================
// File: components/FullscreenModal.jsx
// ====================================
import React from "react";
import { createPortal } from "react-dom"; // MODIFICATION: Import createPortal
import Icons from "../Icons";
import HeaderEditor from "../HeaderEditor";
import SectionContent from "../SectionContent";
import { SECTION_TEMPLATES } from "../constants";
import { NAVIGATION_SECTIONS } from "../ExecutivePremiumTemplate";

const FullscreenModal = ({
  isFullscreen,
  setIsFullscreen,
  fullscreenTab,
  setFullscreenTab,
  resumeData,
  updateData,
  updateSection,
  addSection,
  removeSection,
  addSectionItem,
  updateSectionItem,
  removeSectionItem,
  showFullscreenAddMenu,
  setShowFullscreenAddMenu,
  fullscreenDropdownRef,
  isMobile,
  closeFullscreenAndPreview,
}) => {
  // MODIFICATION: If not in fullscreen, render nothing. This is part of the portal pattern.
  if (!isFullscreen) {
    return null;
  }

  // Handle close action based on device type
  const handleClose = () => {
    if (isMobile && closeFullscreenAndPreview) {
      closeFullscreenAndPreview(); // On mobile, close and show preview
    } else {
      setIsFullscreen(false); // On desktop, just close
    }
  };

  // Render fullscreen content based on active tab
  const renderFullscreenContent = () => {
    switch (fullscreenTab) {
      case "header":
        return <HeaderEditor resumeData={resumeData} updateData={updateData} />;

      case "main":
        return (
          <div className="fullscreen-sections">
            <div className="fullscreen-add-menu" ref={fullscreenDropdownRef}>
              <button
                onClick={() => setShowFullscreenAddMenu(!showFullscreenAddMenu)}
                className="fullscreen-add-section-btn"
              >
                <Icons.Plus className="w-5 h-5" />
                Add Section
              </button>

              {showFullscreenAddMenu && (
                <div className="fullscreen-add-dropdown">
                  <div
                    style={{
                      padding: "12px 16px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#6b7280",
                      textTransform: "uppercase",
                      borderBottom: "1px solid #e5e7eb",
                      background: "#f9fafb",
                    }}
                  >
                    Choose a Section to Add
                  </div>
                  <div style={{ padding: "8px" }}>
                    {Object.entries(SECTION_TEMPLATES.main).map(
                      ([key, template]) => (
                        <button
                          key={key}
                          onClick={() => {
                            addSection("mainContent", key);
                            setShowFullscreenAddMenu(false);
                          }}
                          className="template-option"
                          style={{
                            padding: "12px",
                            width: "100%",
                            textAlign: "left",
                            marginBottom: "4px",
                            borderRadius: "6px",
                            border: "1px solid #e5e7eb",
                            background: "white",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            if (!isMobile) {
                              e.currentTarget.style.background = "#f9fafb";
                              e.currentTarget.style.borderColor = "#065f46";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isMobile) {
                              e.currentTarget.style.background = "white";
                              e.currentTarget.style.borderColor = "#e5e7eb";
                            }
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "12px",
                            }}
                          >
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                flexShrink: 0,
                                background: "#f0f9ff",
                                borderRadius: "6px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#065f46",
                              }}
                            >
                              {template.icon}
                            </div>
                            <div style={{ flex: 1 }}>
                              <h4
                                style={{
                                  fontSize: "14px",
                                  margin: "0 0 4px 0",
                                  fontWeight: 600,
                                  color: "#1a1a1a",
                                }}
                              >
                                {template.title}
                              </h4>
                              <p
                                style={{
                                  fontSize: "12px",
                                  margin: 0,
                                  color: "#6b7280",
                                  lineHeight: "1.4",
                                }}
                              >
                                {template.description}
                              </p>
                            </div>
                          </div>
                        </button>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>

            {resumeData.mainContent?.length === 0 ? (
              <div className="empty-state-card">
                <Icons.Briefcase className="empty-state-icon" />
                <h3 className="empty-state-title">No Experience Added Yet</h3>
                <p className="empty-state-text">
                  Start building your resume by adding your work experience,
                  projects, or achievements.
                </p>
                <button
                  onClick={() => setShowFullscreenAddMenu(true)}
                  className="empty-state-action"
                >
                  <Icons.Plus className="w-4 h-4" />
                  Add Your First Section
                </button>
              </div>
            ) : (
              resumeData.mainContent.map((section) => (
                <div key={section.id} className="fullscreen-section-item">
                  <div className="fullscreen-section-header">
                    <h3 className="fullscreen-section-title">
                      {section.icon}
                      {section.title}
                    </h3>
                    <div className="fullscreen-section-controls">
                      <button
                        onClick={() => {
                          if (
                            window.confirm(`Remove "${section.title}" section?`)
                          ) {
                            removeSection("mainContent", section.id);
                          }
                        }}
                        className="fullscreen-section-action danger"
                        title="Remove section"
                      >
                        <Icons.Trash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <SectionContent
                    section={section}
                    location="mainContent"
                    updateSection={updateSection}
                    addSectionItem={addSectionItem}
                    updateSectionItem={updateSectionItem}
                    removeSectionItem={removeSectionItem}
                  />
                </div>
              ))
            )}
          </div>
        );

      case "sidebar":
        return (
          <div className="fullscreen-sections">
            <div className="fullscreen-add-menu" ref={fullscreenDropdownRef}>
              <button
                onClick={() => setShowFullscreenAddMenu(!showFullscreenAddMenu)}
                className="fullscreen-add-section-btn"
              >
                <Icons.Plus className="w-5 h-5" />
                Add Section
              </button>

              {showFullscreenAddMenu && (
                <div className="fullscreen-add-dropdown">
                  <div
                    style={{
                      padding: "12px 16px",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#6b7280",
                      textTransform: "uppercase",
                      borderBottom: "1px solid #e5e7eb",
                      background: "#f9fafb",
                    }}
                  >
                    Choose a Section to Add
                  </div>
                  <div style={{ padding: "8px" }}>
                    {Object.entries(SECTION_TEMPLATES.sidebar).map(
                      ([key, template]) => (
                        <button
                          key={key}
                          onClick={() => {
                            addSection("sidebarContent", key);
                            setShowFullscreenAddMenu(false);
                          }}
                          className="template-option"
                          style={{
                            padding: "12px",
                            width: "100%",
                            textAlign: "left",
                            marginBottom: "4px",
                            borderRadius: "6px",
                            border: "1px solid #e5e7eb",
                            background: "white",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            if (!isMobile) {
                              e.currentTarget.style.background = "#f9fafb";
                              e.currentTarget.style.borderColor = "#065f46";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isMobile) {
                              e.currentTarget.style.background = "white";
                              e.currentTarget.style.borderColor = "#e5e7eb";
                            }
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "12px",
                            }}
                          >
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                flexShrink: 0,
                                background: "#f0f9ff",
                                borderRadius: "6px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#065f46",
                              }}
                            >
                              {template.icon}
                            </div>
                            <div style={{ flex: 1 }}>
                              <h4
                                style={{
                                  fontSize: "14px",
                                  margin: "0 0 4px 0",
                                  fontWeight: 600,
                                  color: "#1a1a1a",
                                }}
                              >
                                {template.title}
                              </h4>
                              <p
                                style={{
                                  fontSize: "12px",
                                  margin: 0,
                                  color: "#6b7280",
                                  lineHeight: "1.4",
                                }}
                              >
                                {template.description}
                              </p>
                            </div>
                          </div>
                        </button>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>

            {resumeData.sidebarContent?.length === 0 ? (
              <div className="empty-state-card">
                <Icons.GraduationCap className="empty-state-icon" />
                <h3 className="empty-state-title">
                  No Skills or Education Added
                </h3>
                <p className="empty-state-text">
                  Add your skills, education, certifications, and other
                  qualifications.
                </p>
                <button
                  onClick={() => setShowFullscreenAddMenu(true)}
                  className="empty-state-action"
                >
                  <Icons.Plus className="w-4 h-4" />
                  Add Your First Section
                </button>
              </div>
            ) : (
              resumeData.sidebarContent.map((section) => (
                <div key={section.id} className="fullscreen-section-item">
                  <div className="fullscreen-section-header">
                    <h3 className="fullscreen-section-title">
                      {section.icon}
                      {section.title}
                    </h3>
                    <div className="fullscreen-section-controls">
                      <button
                        onClick={() => {
                          if (
                            window.confirm(`Remove "${section.title}" section?`)
                          ) {
                            removeSection("sidebarContent", section.id);
                          }
                        }}
                        className="fullscreen-section-action danger"
                        title="Remove section"
                      >
                        <Icons.Trash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <SectionContent
                    section={section}
                    location="sidebarContent"
                    updateSection={updateSection}
                    addSectionItem={addSectionItem}
                    updateSectionItem={updateSectionItem}
                    removeSectionItem={removeSectionItem}
                  />
                </div>
              ))
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // MODIFICATION: Wrap the entire modal JSX in createPortal, attaching it to document.body
  return createPortal(
    <div className="fullscreen-overlay">
      <div className="fullscreen-container unified-fullscreen">
        <div className="fullscreen-header">
          <div className="fullscreen-nav-tabs">
            {NAVIGATION_SECTIONS.map((section) => (
              <button
                key={section.id}
                onClick={() => setFullscreenTab(section.id)}
                className={`fullscreen-nav-tab ${
                  fullscreenTab === section.id ? "active" : ""
                }`}
              >
                {section.icon}
                <span>{section.label}</span>
              </button>
            ))}
          </div>
          <button onClick={handleClose} className="fullscreen-close-btn">
            {isMobile ? (
              <>
                <Icons.Check className="w-5 h-5" />
                <span>Done & Preview</span>
              </>
            ) : (
              <>
                <Icons.X className="w-5 h-5" />
                <span>Exit Fullscreen</span>
              </>
            )}
          </button>
        </div>

        <div className="fullscreen-body">
          <div className="fullscreen-content">{renderFullscreenContent()}</div>
        </div>

        <div className="fullscreen-footer">
          <p className="fullscreen-hint">
            {isMobile ? (
              "Tap 'Done & Preview' to see your resume"
            ) : (
              <>
                Press <kbd>ESC</kbd> to exit • Use tabs to navigate between
                sections
              </>
            )}
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Add Check and X icons if not present
Icons.Check =
  Icons.Check ||
  (({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  ));

Icons.X =
  Icons.X ||
  (({ className = "w-4 h-4" }) => (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  ));

export default FullscreenModal;
